import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { QuillModule } from 'ngx-quill';

import { AccountPasswordComponent } from 'app/account-profile/account-password/account-password.component';
import { AccountPasswordModalComponent }
  from 'app/account-profile/account-password-modal/account-password-modal.component';
import { AnsweredRfpPreviewModalComponent } from
  'app/offer/offer-preview/answered-rfp-preview-modal/answered-rfp-preview-modal/answered-rfp-preview-modal.component';
import { OfferPreviewSendComponent }
  from 'app/offer/offer-preview/offer-preview-send/offer-preview-send.component';
import { OfferPreviewSendModalComponent }
  from 'app/offer/offer-preview/offer-preview-send-modal/offer-preview-send-modal.component';
import { OfferPreviewSettingsComponent }
  from 'app/offer/offer-preview/offer-preview-settings/offer-preview-settings.component';
import { OfferPreviewSettingsModalComponent }
  from 'app/offer/offer-preview/offer-preview-settings-modal/offer-preview-settings-modal.component';
import { RfpComponent } from 'app/offer/rfp/rfp.component';
import { RfpModalComponent } from 'app/offer/rfp-modal/rfp-modal.component';
import { MeasurementCalculationsComponent }
  from 'app/product-configuration/section/measurement-calculations/measurement-calculations.component';
import { MeasurementCalculationsModalComponent }
  from 'app/product-configuration/section/measurement-calculations-modal/measurement-calculations-modal.component';
import { AdvancedDimensionsComponent } from 'shared/advanced-dimensions/advanced-dimensions.component';
import { AddArticleToOfferComponent } from 'shared/forms/article/add-article-to-offer/add-article-to-offer.component';
import { BranchBasicInfoModalComponent } from 'shared/forms/branch/basic-info-modal/basic-info-modal.component';
import { BranchInviteMemberComponent } from 'shared/forms/branch/invite-member/invite-member.component';
import {
  BranchInviteMemberModalComponent
} from 'shared/forms/branch/invite-member-modal/invite-member-modal.component';
import {
  SearchCustomersWithoutConditionComponent
} from 'shared/forms/customer/search-customers-without-condition/search-customers-without-condition.component';
import {
  AddLooseArticleToOfferComponent
} from 'shared/forms/loose-categories/add-loose-article-to-offer/add-loose-article-to-offer.component';
import {
  AddLooseArticleToOfferModalComponent
} from 'shared/forms/loose-categories/add-loose-article-to-offer-modal/add-loose-article-to-offer-modal.component';
import {
  LooseCategoriesModalComponent
} from 'shared/forms/loose-categories/loose-categories-modal/loose-categories-modal.component';
import { LooseCategoriesComponent } from 'shared/forms/loose-categories/loose-categories.component';

import { AddProductMenuComponent } from './add-product/add-product-menu/add-product-menu.component';
import { AddProductComponent } from './add-product/add-product.component';
import { AsideNavComponent } from './aside-nav/aside-nav.component';
import { ButtonDropdownComponent } from './button-dropdown/button-dropdown.component';
import { ConfirmModalComponent } from './confirm-modal/confirm-modal.component';
import { DirectionRadioComponent } from './direction-radio/direction-radio.component';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { DynamicInsertionDirective } from './directives/dynamic-insertion.directive';
import { PreloadImageDirective } from './directives/preload-image.directive';
import { RouteTransformerDirective } from './directives/route-transformer.directive';
import { ScrollSpyDirective } from './directives/scroll-spy.directive';
import { SvgImageDirective } from './directives/svg-image.directive';
import { CheckboxComponent } from './form-fields/checkbox/checkbox.component';
import { DropdownComponent } from './form-fields/dropdown/dropdown.component';
import { FormErrorsComponent } from './form-fields/form-errors/form-errors.component';
import { FormPasswordErrorsComponent } from './form-fields/form-password-errors/form-password-errors.component';
import { InputComponent } from './form-fields/input/input.component';
import { InputDatepickerComponent } from './form-fields/input-datepicker/input-datepicker.component';
import { InputFileComponent } from './form-fields/input-file/input-file.component';
import { InputPasswordComponent } from './form-fields/input-password/input-password.component';
import { InputSearchComponent } from './form-fields/input-search/input-search.component';
import { InputTagsComponent } from './form-fields/input-tags/input-tags.component';
import { MultiSelectComponent } from './form-fields/multi-select/multi-select.component';
import { RadioComponent } from './form-fields/radio/radio.component';
import { TextEditorComponent } from './form-fields/text-editor/text-editor.component';
import { TextareaComponent } from './form-fields/textarea/textarea.component';
import { FormRowComponent } from './form-row/form-row.component';
import {
  AddFavoriteModalComponent
} from './forms/add-favorite/add-favorite-modal/add-favorite-modal.component';
import { AddFavoriteComponent } from './forms/add-favorite/add-favorite.component';
import {
  AddArticleToOfferModalComponent
} from './forms/article/add-article-to-offer-modal/add-article-to-offer-modal.component';
import { ArticleModalComponent } from './forms/article/article-modal/article-modal.component';
import { ArticleComponent } from './forms/article/article.component';
import { BranchBasicInfoComponent } from './forms/branch/basic-info/basic-info.component';
import { ContactPersonBasicInfoComponent } from './forms/contact-person/basic-info/basic-info.component';
import {
  ContactPersonBasicInfoModalComponent
} from './forms/contact-person/basic-info-modal/basic-info-modal.component';
import { ContactPersonSearchComponent } from './forms/contact-person/search/search.component';
import { CustomerBasicInfoComponent } from './forms/customer/basic-info/basic-info.component';
import { CustomerBasicInfoModalComponent } from './forms/customer/basic-info-modal/basic-info-modal.component';
import { CustomerConditionsComponent } from './forms/customer/conditions/conditions.component';
import { CustomerConditionsModalComponent } from './forms/customer/conditions-modal/conditions-modal.component';
import {
  CustomerCustomConditionsModalComponent
} from './forms/customer/custom-conditions-modal/custom-conditions-modal.component';
import { CustomerSearchComponent } from './forms/customer/search/search.component';
import { EditFavoriteModalComponent } from './forms/edit-favorite/edit-favorite-modal/edit-favorite-modal.component';
import { EditFavoriteComponent } from './forms/edit-favorite/edit-favorite.component';
import {
  ExpirationDateModalComponent
} from './forms/expiration-date/expiration-date-modal/expiration-date-modal.component';
import { ExpirationDateComponent } from './forms/expiration-date/expiration-date.component';
import {
  ObjektAdditionalAddressComponent
} from './forms/objekt/basic-info/additional-address/additional-address.component';
import { ObjektBasicInfoComponent } from './forms/objekt/basic-info/basic-info.component';
import { ObjektBasicInfoModalComponent } from './forms/objekt/basic-info-modal/basic-info-modal.component';
import { ObjektSearchComponent } from './forms/objekt/search/search.component';
import { GroupedDropdownComponent } from './grouped-dropdown/grouped-dropdown.component';
import { GroupedDropdownLabelComponent } from './grouped-dropdown-label/grouped-dropdown-label.component';
import { HeaderComponent } from './header/header.component';
import { HeaderMainNavComponent } from './header/main-nav/main-nav.component';
import { HeaderSideNavComponent } from './header/side-nav/side-nav.component';
import { ImportStatusComponent } from './import-status/import-status.component';
import { TemplateListComponent } from './lists/template-list/template-list.component';
import { LoadingOverlayComponent } from './loading-overlay/loading-overlay.component';
import { MultimodalComponent } from './multimodal/multimodal.component';
import { PdfPreviewComponent } from './pdf-preview/pdf-preview.component';
import { IsArrayPipe } from './pipes/is-array.pipe';
import { NoSanitizePipe } from './pipes/no-sanitize.pipe';
import { TranslateControlPipe } from './pipes/translate-control.pipe';
import { PopoverComponent } from './popover/popover.component';
import { SuccessModalComponent } from './success-modal/success-modal.component';
import { TableFiltersComponent } from './table/table-filters/table-filters.component';
import { TableFormComponent } from './table/table-form/table-form.component';
import { TableNoResultsComponent } from './table/table-no-results/table-no-results.component';
import { TableOffersComponent } from './table/table-offers/table-offers.component';
import { TablePaginationComponent } from './table/table-pagination/table-pagination.component';
import { TableComponent } from './table/table.component';
import { TogglerComponent } from './toggler/toggler.component';
import { TooltipComponent } from './tooltip/tooltip.component';
import { UniDropdownComponent } from './uni-dropdown/uni-dropdown.component';
import { UpdateMessageComponent } from './update-message/update-message.component';
import { UploadCsvComponent } from './upload-csv/upload-csv.component';
import { CloseOfferComponent } from '../offer/calculation/summary/close-offer/close-offer.component';
import { CloseOfferModalComponent } from '../offer/calculation/summary/close-offer-modal/close-offer-modal.component';
import { AnsweredRfpPreviewComponent } from
  '../offer/offer-preview/answered-rfp-preview/answered-rfp-preview/answered-rfp-preview.component';
import { ScrollPositionDirective } from './directives/scroll-position.directive';
import { DeleteAccountModalComponent } from './forms/branch/delete-account-modal/delete-account-modal.component';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { PriceViewHelperComponent } from './helpers/price-view/price-view-helper.component';
import { DirectionRadioReadonlyComponent } from './direction-radio-readonly/direction-radio-readonly.component';
import {
  EditArticleInOfferModalComponent
} from 'shared/forms/article/edit-article-in-offer-modal/edit-article-in-offer-modal.component';
import {
  EditArticleInOfferFormComponent
} from 'shared/forms/article/edit-article-in-offer-form/edit-article-in-offer-form.component';
import { SvgIconComponent } from 'shared/icon-svg/svg-icon.component'
import {
  BranchSftpSettingsFormComponent
} from 'app/settings/branch/sftp-settings/sftp-settings-form/sftp-settings-form.component';
import {
  BranchSftpSettingsModalComponent
} from 'app/settings/branch/sftp-settings/sftp-settings-modal/sftp-settings-modal.component';

@NgModule({
  declarations: [
    TableComponent,
    TableOffersComponent,
    TableFormComponent,
    RadioComponent,
    InputComponent,
    InputSearchComponent,
    InputTagsComponent,
    DropdownComponent,
    HeaderComponent,
    ScrollSpyDirective,
    CheckboxComponent,
    TextareaComponent,
    CustomerBasicInfoComponent,
    CustomerBasicInfoModalComponent,
    MultimodalComponent,
    ConfirmModalComponent,
    DynamicInsertionDirective,
    CustomerConditionsComponent,
    CustomerConditionsModalComponent,
    CustomerCustomConditionsModalComponent,
    ObjektBasicInfoComponent,
    ObjektBasicInfoModalComponent,
    ObjektAdditionalAddressComponent,
    ButtonDropdownComponent,
    CustomerSearchComponent,
    SearchCustomersWithoutConditionComponent,
    ContactPersonSearchComponent,
    ContactPersonBasicInfoComponent,
    ContactPersonBasicInfoModalComponent,
    ObjektSearchComponent,
    FormRowComponent,
    AddProductComponent,
    AddProductMenuComponent,
    SvgImageDirective,
    AutoFocusDirective,
    PreloadImageDirective,
    RouteTransformerDirective,
    TranslateControlPipe,
    NoSanitizePipe,
    IsArrayPipe,
    HeaderMainNavComponent,
    HeaderSideNavComponent,
    FormErrorsComponent,
    TableNoResultsComponent,
    ArticleComponent,
    AsideNavComponent,
    TemplateListComponent,
    BranchBasicInfoComponent,
    BranchInviteMemberComponent,
    DeleteAccountModalComponent,
    CloseOfferComponent,
    ArticleModalComponent,
    ExpirationDateComponent,
    ExpirationDateModalComponent,
    BranchBasicInfoModalComponent,
    BranchInviteMemberModalComponent,
    CloseOfferModalComponent,
    MultiSelectComponent,
    FormPasswordErrorsComponent,
    InputPasswordComponent,
    TextEditorComponent,
    PdfPreviewComponent,
    InputFileComponent,
    UploadCsvComponent,
    ImportStatusComponent,
    UploadCsvComponent,
    SuccessModalComponent,
    LoadingOverlayComponent,
    TablePaginationComponent,
    MeasurementCalculationsComponent,
    MeasurementCalculationsModalComponent,
    AccountPasswordComponent,
    AccountPasswordModalComponent,
    OfferPreviewSettingsComponent,
    OfferPreviewSettingsModalComponent,
    OfferPreviewSendComponent,
    OfferPreviewSendModalComponent,
    AnsweredRfpPreviewComponent,
    AnsweredRfpPreviewModalComponent,
    RfpComponent,
    RfpModalComponent,
    PopoverComponent,
    InputDatepickerComponent,
    TableFiltersComponent,
    AddArticleToOfferComponent,
    AddArticleToOfferModalComponent,
    LooseCategoriesComponent,
    LooseCategoriesModalComponent,
    AddLooseArticleToOfferComponent,
    AddLooseArticleToOfferModalComponent,
    TooltipComponent,
    TogglerComponent,
    UniDropdownComponent,
    UpdateMessageComponent,
    AddFavoriteModalComponent,
    AddFavoriteComponent,
    EditFavoriteModalComponent,
    EditFavoriteComponent,
    GroupedDropdownComponent,
    GroupedDropdownLabelComponent,
    DirectionRadioComponent,
    DirectionRadioReadonlyComponent,
    AdvancedDimensionsComponent,
    ScrollPositionDirective,
    ForbiddenComponent,
    PriceViewHelperComponent,
    EditArticleInOfferModalComponent,
    EditArticleInOfferFormComponent,
    BranchSftpSettingsFormComponent,
    BranchSftpSettingsModalComponent
  ],
  exports: [
    TableComponent,
    TableOffersComponent,
    TableFormComponent,
    RadioComponent,
    DropdownComponent,
    InputComponent,
    InputSearchComponent,
    InputTagsComponent,
    ReactiveFormsModule,
    HeaderComponent,
    ScrollSpyDirective,
    CheckboxComponent,
    TextareaComponent,
    CustomerBasicInfoComponent,
    CustomerConditionsComponent,
    ObjektBasicInfoComponent,
    ButtonDropdownComponent,
    CustomerSearchComponent,
    SearchCustomersWithoutConditionComponent,
    ContactPersonSearchComponent,
    ContactPersonBasicInfoComponent,
    MultimodalComponent,
    ObjektSearchComponent,
    FormRowComponent,
    AddProductComponent,
    AddProductMenuComponent,
    SvgImageDirective,
    AutoFocusDirective,
    PreloadImageDirective,
    RouteTransformerDirective,
    CdkTableModule,
    TranslateModule,
    TranslateControlPipe,
    NoSanitizePipe,
    HeaderMainNavComponent,
    HeaderSideNavComponent,
    FormErrorsComponent,
    AsideNavComponent,
    TemplateListComponent,
    BranchBasicInfoComponent,
    BranchInviteMemberComponent,
    DeleteAccountModalComponent,
    CloseOfferComponent,
    MultiSelectComponent,
    InputPasswordComponent,
    PdfPreviewComponent,
    InputFileComponent,
    UploadCsvComponent,
    ImportStatusComponent,
    UploadCsvComponent,
    LoadingOverlayComponent,
    PopoverComponent,
    InputDatepickerComponent,
    TableFiltersComponent,
    TooltipComponent,
    TogglerComponent,
    UniDropdownComponent,
    UpdateMessageComponent,
    AddFavoriteComponent,
    EditFavoriteComponent,
    GroupedDropdownComponent,
    GroupedDropdownLabelComponent,
    DirectionRadioComponent,
    DirectionRadioReadonlyComponent,
    AdvancedDimensionsComponent,
    ScrollPositionDirective,
    ForbiddenComponent,
    PriceViewHelperComponent,
    EditArticleInOfferModalComponent,
    EditArticleInOfferFormComponent,
    SvgIconComponent
  ],
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CollapseModule.forRoot(),
    ModalModule.forRoot(),
    AccordionModule.forRoot(),
    CdkTableModule,
    TranslateModule,
    QuillModule.forRoot(),
    PdfViewerModule,
    TabsModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TooltipModule.forRoot(),
    SvgIconComponent
  ],
  providers: [
    DatePipe
  ]
})
export class SharedModule { }
